import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enGB from "date-fns/locale/en-GB";
import { format, isValid } from "date-fns";
import { dateLocale } from "i18n";
import ConfirmDialog from "components/common/ConfirmDialog";
import SelectableCard from "./SelectableCard";
import EncounterSetupRows from "./EncounterSetupRows";
import { EVENT_TIME_FORMAT, INPATIENT_EVENT_OPTIONS, parseEventTime } from "./inpatient";

const BLANK_EVENT = { name: "", comment: "" };

/** The event and its comment, shared by the add form and the edit dialog. */
const EventFields = ({ control, autoFocus = false, commentRows = 2 }) => (
  <>
    <Controller
      name="name"
      control={control}
      render={({ field }) => (
        <Autocomplete
          freeSolo
          options={INPATIENT_EVENT_OPTIONS}
          value={field.value}
          inputValue={field.value}
          onChange={(_, value) => field.onChange(value || "")}
          onInputChange={(_, value) => field.onChange(value)}
          renderInput={(params) => (
            <TextField {...params} autoFocus={autoFocus} label="Event" placeholder="e.g., Fall, Hypoxia" />
          )}
        />
      )}
    />
    <Controller
      name="comment"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label="Comment"
          placeholder="What happened and what was done"
          multiline
          minRows={commentRows}
          fullWidth
        />
      )}
    />
  </>
);

/**
 * Edit an event's name, time and comment, or remove it. The time is set when the event is added,
 * so this is where it is moved back for an event that happened earlier.
 * `event` is held by the caller until the dialog has finished closing (see EventsStep), so the
 * fields never empty out mid-fade.
 */
const EditEventDialog = ({ open, event, onClose, onExited, onSave, onRemove }) => {
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: { ...BLANK_EVENT, occurredAt: null },
  });
  const name = watch("name");

  useEffect(() => {
    if (open && event) {
      reset({
        name: event.name,
        comment: event.comment || "",
        occurredAt: parseEventTime(event.occurredAt),
      });
    }
  }, [open, event, reset]);

  const submit = handleSubmit(({ name: value, comment, occurredAt }) => {
    onSave(event.id, {
      name: value.trim(),
      comment,
      occurredAt: occurredAt ? format(occurredAt, EVENT_TIME_FORMAT) : "",
    });
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onExited }}>
      <DialogTitle>Edit event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <EventFields control={control} commentRows={3} />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale() || enGB}>
            <Controller
              name="occurredAt"
              control={control}
              rules={{ validate: (v) => v == null || isValid(v) || "Please enter a valid time." }}
              render={({ field, fieldState: { error } }) => (
                <DateTimePicker
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      label: "Time",
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={() => onRemove(event)} sx={{ mr: "auto" }}>
          Remove
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!String(name || "").trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * The Events note type's first step, in place of Record: an inline form to add an event, a card
 * per event with a pencil to edit it, and the same setup rows as Record so the note type can still
 * be changed. The events are the note's only source, so the differential, plan and note are all
 * written from them.
 */
const EventsStep = ({ events, onAddEvent, onUpdateEvent, onRemoveEvent, setup = null }) => {
  // Each dialog's event is kept until its close animation ends, and only `open` flips on close,
  // so neither dialog's content changes while it fades out.
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const { control, handleSubmit, reset, watch } = useForm({ defaultValues: BLANK_EVENT });
  const name = watch("name");

  // Stamped with the time it is added; an event that happened earlier is moved back from its card.
  const add = handleSubmit(({ name: value, comment }) => {
    if (!String(value || "").trim()) return;
    onAddEvent(value, comment, format(new Date(), EVENT_TIME_FORMAT));
    reset(BLANK_EVENT);
  });

  return (
    // Same offset, column width and title as the Record step, so switching the note type to
    // Events does not move the page.
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: { xs: 2, md: "10vh" },
      }}
    >
      <Stack sx={{ width: "100%", maxWidth: 600 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h4" sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "1.75rem", sm: "2.125rem" } }}>
            Event
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            Please add the event this note is about.
          </Typography>
        </Box>

        <Stack component="form" spacing={2} onSubmit={add} sx={{ mb: 4 }}>
          <EventFields control={control} />
          <Button
            type="submit"
            variant="contained"
            disabled={!String(name || "").trim()}
            sx={{ alignSelf: "flex-end" }}
          >
            Add Event
          </Button>
        </Stack>

        {events.map((event) => (
          <SelectableCard
            key={event.id}
            title={event.name}
            selectionVariant="none"
            onEdit={() => {
              setEditing(event);
              setEditOpen(true);
            }}
            editTooltip="Edit event"
            additionalContent={
              parseEventTime(event.occurredAt) || event.comment?.trim() ? (
                <>
                  {parseEventTime(event.occurredAt) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {format(parseEventTime(event.occurredAt), "MMM d, HH:mm")}
                    </Typography>
                  )}
                  {event.comment?.trim() && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {event.comment}
                    </Typography>
                  )}
                </>
              ) : null
            }
          />
        ))}

        {setup && (
          <Stack spacing={0.5} sx={{ width: "100%", maxWidth: 560, alignSelf: "center", mt: 4 }}>
            <EncounterSetupRows {...setup} />
          </Stack>
        )}
      </Stack>

      <EditEventDialog
        open={editOpen}
        event={editing}
        onClose={() => setEditOpen(false)}
        onExited={() => setEditing(null)}
        onSave={onUpdateEvent}
        onRemove={(event) => {
          setEditOpen(false);
          setRemoving(event);
          setRemoveOpen(true);
        }}
      />

      <ConfirmDialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        onExited={() => setRemoving(null)}
        title="Remove this event?"
        message={removing ? `${removing.name} and its comment will be removed from the note.` : ""}
        confirmLabel="Remove"
        confirmColor="error"
        onConfirm={() => {
          onRemoveEvent(removing.id);
          setRemoveOpen(false);
        }}
      />
    </Box>
  );
};

export default EventsStep;
